import React, { useState, useRef } from 'react';
import api from '../api';

export default function RemoveBg() {
  const [original, setOriginal] = useState(null);   // preview URL of uploaded file
  const [result, setResult] = useState(null);        // blob URL of processed image
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    setResult(null);
    setError('');
    setOriginal(URL.createObjectURL(file));
    processFile(file);
  };

  const processFile = async (file) => {
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/api/remove-bg', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
      });
      setResult(URL.createObjectURL(res.data));
    } catch (err) {
      setError('Background removal failed. Please try a different image.');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = result;
    a.download = 'removed-bg.png';
    a.click();
  };

  const reset = () => {
    setOriginal(null);
    setResult(null);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(110,231,183,0.06) 0%, transparent 100%), var(--bg)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '48px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 640 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div className="logo-mark" style={{ justifyContent: 'center', marginBottom: 10 }}>
            <div className="logo-icon">⬡</div>
            ID System
          </div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 6 }}>Remove Background</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            Upload any photo and get a transparent-background PNG instantly.
          </p>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Upload zone — shown when no image selected yet */}
        {!original && (
          <div
            className="upload-zone"
            style={{
              padding: 48, cursor: 'pointer',
              border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
              background: dragging ? 'rgba(52,211,153,0.05)' : undefined,
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onClick={() => inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" style={{ color: 'var(--text-muted)', marginBottom: 12 }}>
              <circle cx="12" cy="12" r="3" /><path d="M14.5 4h-5L8 6H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-4l-1.5-2z" />
            </svg>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>Click or drag & drop</p>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>JPG, PNG — max 10 MB</span>
            <input
              ref={inputRef} type="file" accept=".jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={(e) => handleFile(e.target.files[0])}
            />
          </div>
        )}

        {/* Before / After comparison */}
        {original && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Original */}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{
                  padding: '8px 14px', fontSize: '0.72rem', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
                  background: 'var(--bg2)',
                }}>Original</div>
                <div style={{ background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                  <img src={original} alt="original" style={{ maxWidth: '100%', maxHeight: 300, display: 'block' }} />
                </div>
              </div>

              {/* Result */}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{
                  padding: '8px 14px', fontSize: '0.72rem', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
                  background: 'var(--bg2)',
                }}>Background Removed</div>
                <div style={{
                  minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                  backgroundSize: '16px 16px',
                  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                  backgroundColor: '#fff',
                }}>
                  {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: 'var(--text-muted)' }}>
                      <div className="spinner spinner-lg" />
                      <span style={{ fontSize: 13 }}>Removing background…</span>
                    </div>
                  ) : result ? (
                    <img src={result} alt="result" style={{ maxWidth: '100%', maxHeight: 300, display: 'block' }} />
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Processing…</span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10 }}>
              {result && (
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleDownload}>
                  Download PNG
                </button>
              )}
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={reset}>
                Try Another Image
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
